import React from "react";
import { Route, Navigate } from "react-router-dom";
import MainLayout from "@/components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";
import ErrorBoundaryWithFallback from "@/components/ErrorBoundary/ErrorBoundaryWithFallback";

// Lazy-loaded route components
const TranscriptionQueueDashboard = React.lazy(
  () =>
    import(
      "@/modules/transcriptionQueue/features/Dashboard/components/TranscriptionQueueDashboard"
    ),
);
const TranscriptionQueueAnalytics = React.lazy(
  () =>
    import(
      "@/modules/transcriptionQueue/features/Analytics/components/TranscriptionQueueAnalytics"
    ),
);
const AlertConfigurationPage = React.lazy(
  () =>
    import(
      "@/modules/transcriptionQueue/features/AlertConfiguration/components/AlertConfigurationPage"
    ),
);
const TranscriptionQueueSettings = React.lazy(
  () =>
    import(
      "@/modules/transcriptionQueue/features/Settings/components/TranscriptionQueueSettings"
    ),
);
const TranscriptionRulesPage = React.lazy(
  () =>
    import(
      "@/modules/transcriptionQueue/features/TranscriptionRules/components/TranscriptionRulesPage"
    ),
);

/**
 * TranscriptionQueueRoutes
 *
 * Routes for the Transcription Queue Monitor module
 * Provides real-time monitoring of audio transcription jobs
 */
const TranscriptionQueueRoutes = (
  <Route
    element={
      <AuthenticationGuard
        component={() => <MainLayout section="transcription-queue" />}
      />
    }
    path="/transcription-queue"
  >
    {/* Dashboard - Default route */}
    <Route
      element={<Navigate replace to="/transcription-queue/dashboard" />}
      index
    />

    {/* Main Dashboard */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={[
            "ROLE_SUPER_ADMIN",
            "ROLE_ADMIN",
            "ROLE_OPERATIONS",
            "ROLE_SUPPORT",
          ]}
        >
          <ErrorBoundaryWithFallback>
            <TranscriptionQueueDashboard />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="dashboard"
    />

    {/* Analytics Dashboard */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_SUPER_ADMIN", "ROLE_ADMIN", "ROLE_OPERATIONS"]}
        >
          <ErrorBoundaryWithFallback>
            <TranscriptionQueueAnalytics />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="analytics"
    />

    {/* Alerts Configuration */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN", "ROLE_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <AlertConfigurationPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="alerts"
    />

    {/* Settings */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN", "ROLE_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <TranscriptionQueueSettings />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="settings"
    />

    {/* Transcription Rules - Phone line transcription settings */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN", "ROLE_ADMIN"]}>
          <ErrorBoundaryWithFallback>
            <TranscriptionRulesPage />
          </ErrorBoundaryWithFallback>
        </PermissionGuard>
      }
      path="rules"
    />
  </Route>
);

export default TranscriptionQueueRoutes;
